import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { Editor } from '@tiptap/react'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { EmailLinksEditor } from 'components/EmailLinksEditor'
import { EmailValue, LoanPartiesEmailTo } from 'components/EmailTo'
import type { CustomInput, InputType } from 'config'
import { EmailTemplates } from 'pages/Admin/ConditionsAndTemplates/EmailTemplates'
import { Notes } from 'pages/Admin/ConditionsAndTemplates/Notes'
import { INoteButton } from 'pages/Admin/ConditionsAndTemplates/Notes/types'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getInternalUsers, getMentionGroups, loanOverviewSectionByLoanNumber, postAiPrompt } from 'services'
import { ButtonGroup, Modal } from 'stories/components'
import { getPartiesMap, InputConvert, InputValidate, resolveOverviewCopyTemplate } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { MentionRichText } from './MentionRichText'
import { type MentionGroup, type MentionUser, INote, NotePageType } from './types'

export const defaultInputs = (loanNumber: number): Record<string, InputType> => {
  return {
    subject: {
      inputType: 'text',
      title: 'Subject',
      span: 2,
    },
    content: {
      inputType: 'textarea',
      type: 'text',
      title: 'Note',
      value: '',
      span: 2,
      required: true,
    },
    notifiers: {
      inputType: 'custom',
      title: 'Send Notification To',
      span: 'full',
    },
    files: {
      inputType: 'filetable',
      title: 'Files',
      required: false,
      acceptFileTypes: '.pdf, .msg, .xls, .xlsx',
      multiple: true,
      span: 'full',
      showCategory: false,
      showStatus: false,
      filePath: `loan/${loanNumber}/notes`,
    },
  }
}

export const AddNote = ({
  index,
  item,
  onClose,
  onUpdate,
  isEmail = false,
  replyData = null,
  loanNumber: _loanNumber,
  notePageType = NotePageType.GLOBAL,
  defaultData = {},
}: {
  index: number
  item: INote | null
  onClose: Function
  onUpdate: Function
  isEmail?: boolean
  replyData?: INote | null
  loanNumber?: number
  notePageType?: NotePageType
  defaultData?: Record<string, any>
}) => {
  const loanNumber = Number(_loanNumber || setLoanNumber())
  const overviewCopyTemplate = useSelector(
    (state: any) => state.globalConfigReducer[GlobalConfigType.GlobalSettingV2]?.overviewCopyTemplate,
  )
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs(loanNumber))
  const [notifiers, setNotifiers] = useState<EmailValue>({})
  const [links, setLinks] = useState<INoteButton[]>([])
  const [showError, setShowError] = useState(false)
  const [showTemplate, setShowTemplate] = useState(false)
  const [templateType, setTemplateType] = useState<'note' | 'email'>(isEmail ? 'email' : 'note')
  // The note body is a rich-text editor, so the placeholder chips insert
  // through its API instead of splicing a string at a textarea caret.
  const contentEditorRef = useRef<Editor | null>(null)

  // @mention typeahead state
  const [mentionUsers, setMentionUsers] = useState<MentionUser[]>([])
  const [mentionGroups, setMentionGroups] = useState<MentionGroup[]>([])
  const [mentions, setMentions] = useState<string[]>([])
  const [taggedGroups, setTaggedGroups] = useState<string[]>([])

  // AI assist state
  const [aiInstruction, setAiInstruction] = useState('')
  const [aiTone, setAiTone] = useState<'' | 'Formal' | 'Friendly' | 'Firm'>('')
  const [aiLoading, setAiLoading] = useState<'' | 'draft' | 'improve' | 'subject' | 'summary'>('')
  const [placeholdersOpen, setPlaceholdersOpen] = useState(false)

  const stripHtml = (s: string) =>
    String(s || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  const toneClause = aiTone ? ` Use a ${aiTone.toLowerCase()} tone.` : ''

  // Available note placeholders (auto-filled on send) — let the AI use them where they fit.
  const placeholderKeys = Object.keys(getPartiesMap({}))
  const placeholderClause = placeholderKeys.length
    ? ` Where it fits naturally, personalize using these placeholders written EXACTLY as shown (they are automatically ` +
      `replaced with real values when the ${isEmail ? 'email' : 'note'} is sent): ${placeholderKeys
        .map((k) => `{{${k}}}`)
        .join(', ')}. Only use a placeholder when appropriate and never invent new ones.`
    : ''

  const runAi = async (prompt: string): Promise<string> => {
    const { data } = await postAiPrompt(`note-ai-${loanNumber}`, 'prompt', {
      prompt,
      files: [],
      baseFiles: [],
      history: [],
      options: { jsonResponse: false },
    })
    const text = typeof data === 'string' ? data.trim() : String((data as any)?.text || '').trim()
    // The AI service returns empty on failure (e.g. no credits / AI service down) — surface that
    // as an error instead of silently doing nothing.
    if (!text) throw new Error('The AI service returned no content. Check the AI service is configured and funded.')
    return text
  }

  const noun = isEmail ? 'email' : 'loan servicing note'

  const onAiDraft = async () => {
    if (!aiInstruction.trim() || aiLoading) return
    setAiLoading('draft')
    try {
      const existing = stripHtml(inputs.content.value as string)
      const prompt =
        `Write a clear, professional ${noun} based on this instruction:\n\n"${aiInstruction.trim()}"\n\n` +
        (existing ? `Build on this current draft:\n${existing}\n\n` : '') +
        `Return only the ${
          isEmail ? 'email body' : 'note'
        } text — no preamble, no subject line, no quotes.${toneClause}${placeholderClause}`
      const text = await runAi(prompt)
      if (text) onChange('content', text)
    } catch (e: any) {
      toast(e?.message || 'AI could not draft the note. Please try again.', { type: 'error' })
    } finally {
      setAiLoading('')
    }
  }

  const onAiImprove = async () => {
    const existing = stripHtml(inputs.content.value as string)
    if (!existing || aiLoading) return
    setAiLoading('improve')
    try {
      const prompt =
        `Improve the following loan ${isEmail ? 'email' : 'note'} to be clear, concise and professional. ` +
        `Keep the meaning and all specifics (numbers, dates, names). Return only the improved text — no preamble, no quotes.${toneClause}${placeholderClause}\n\n${existing}`
      const text = await runAi(prompt)
      if (text) onChange('content', text)
    } catch (e: any) {
      toast(e?.message || 'AI could not improve the note. Please try again.', { type: 'error' })
    } finally {
      setAiLoading('')
    }
  }

  const onAiSummarize = async () => {
    if (aiLoading) return
    const thread = stripHtml(`${(replyData as any)?.content || ''} ${(replyData as any)?.history || ''}`)
    if (!thread) {
      toast('Nothing to summarize in this thread.', { type: 'info' })
      return
    }
    setAiLoading('summary')
    try {
      const prompt =
        `Summarize the following email thread for a loan servicer as a few concise bullet points ` +
        `(who said what, key requests, and any action items). Return only the bullet points — no preamble.\n\n${thread}`
      const text = await runAi(prompt)
      if (text) onChange('content', text)
    } catch (e: any) {
      toast(e?.message || 'AI could not summarize the thread. Please try again.', { type: 'error' })
    } finally {
      setAiLoading('')
    }
  }

  const onAiSubject = async () => {
    const existing = stripHtml(inputs.content.value as string)
    if (!existing || aiLoading) return
    setAiLoading('subject')
    try {
      const prompt =
        `Write a short, specific subject line (max ~8 words) for the following loan ${isEmail ? 'email' : 'note'}. ` +
        `Return only the subject text — no quotes, no preamble.\n\n${existing}`
      const text = await runAi(prompt)
      if (text) onChange('subject', text.replace(/^["']|["']$/g, ''))
    } catch (e: any) {
      toast(e?.message || 'AI could not generate a subject. Please try again.', { type: 'error' })
    } finally {
      setAiLoading('')
    }
  }

  useEffect(() => {
    getInternalUsers()
      .then((users) => setMentionUsers(users || []))
      .catch(() => setMentionUsers([]))
    getMentionGroups()
      .then((groups) => setMentionGroups(groups || []))
      .catch(() => setMentionGroups([]))
  }, [])

  useEffect(() => {
    if (item) {
      const newInputs = cloneDeep(inputs)
      newInputs.subject.value = item.subject
      newInputs.content.value = item.content
      newInputs.files.value = item.files
      setInputs(newInputs)
      setNotifiers(item.notifiers)
      setLinks((item as any).links || [])
    } else {
      if (replyData) return
      const newInputs = cloneDeep(inputs)
      if (defaultData.files) newInputs.files.value = defaultData.files
      loanOverviewSectionByLoanNumber(loanNumber).then((detail) => {
        newInputs.subject.value = resolveOverviewCopyTemplate(overviewCopyTemplate, {
          loanNumber: detail.loanNumber,
          byteproFileName: detail.byteproFileName,
          borrowingEntityName: detail.borrowingEntityName,
          entityTitle: detail.entityTitle,
          borrowerName: detail.borrowerName,
          propertyAddress: detail.propertyAddress,
          proposedLoanAmount: detail.proposedLoanAmount ?? detail.loanAmount,
          program: detail.program,
          loanStatus: detail.loanStatus,
        })
        setInputs(newInputs)
      })
    }
  }, [])

  useEffect(() => {
    if (!replyData) return
    const { noteBy, subject } = replyData
    const splits = noteBy.split(' - ')
    const hasName = splits.length > 1
    const email = hasName ? splits[1] : splits[0]

    const temp: any = cloneDeep(notifiers)
    setNotifiers({ ...temp, [email]: 'to' })

    const newInputs = cloneDeep(inputs)
    newInputs.subject.value = subject
    setInputs(newInputs)
  }, [replyData])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const addMention = (email: string) => setMentions((prev) => Array.from(new Set([...prev, email])))

  const addGroup = (key: string) => setTaggedGroups((prev) => Array.from(new Set([...prev, key])))

  /** Drop a `{{Field}}` chip into the note body at the caret. */
  const insertPlaceholder = (key: string) => {
    const editor = contentEditorRef.current
    if (!editor) return
    editor
      .chain()
      .focus()
      .insertContent({ type: 'placeholder', attrs: { name: key } })
      .run()
  }

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    if (isEmail && !Object.values(notifiers).includes('to')) {
      newInputs.notifiers.error = 'Please set "To" of Notification Emails'
      hasError = true
    }
    if (!item) {
      links.map((linkItem) => {
        if (!linkItem.label || !linkItem.url) hasError = true
      })
    }
    setInputs(newInputs)
    setShowError(true)
    if (hasError) return

    // Only keep mentions whose @token still appears in the note text.
    const finalMentions = mentions.filter((email) => {
      const u = mentionUsers.find((x) => x.email === email)
      return u && String(data.content || '').includes(`@${u.name}`)
    })
    const finalGroups = taggedGroups.filter((key) => {
      const g = mentionGroups.find((x) => x.key === key)
      return g && String(data.content || '').includes(`@${g.title}`)
    })

    setLoading(true)
    onUpdate(
      data.subject,
      data.content,
      data.files,
      notifiers,
      isEmail,
      replyData,
      notePageType,
      links,
      finalMentions,
      finalGroups,
    )
  }

  const onChooseTemplate = (template: Record<string, any>) => {
    const temp = cloneDeep(inputs)
    if (templateType === 'note') {
      temp.content.value = template.notes
      setLinks(template.links || [])
    } else {
      // Email templates keep their body across `title` and `content` (most have title only,
      // with `content` empty), so pull from both — otherwise the note prefills blank.
      temp.content.value = [template.title, template.content].filter(Boolean).join('\n\n')
      setLinks(template.links || [])
    }
    setInputs(temp)
    setShowTemplate(false)
  }

  const renderNotifier = () => {
    const { error } = inputs.notifiers
    return (
      <div className="w-full">
        <RenderInput input={{ inputType: 'section', title: 'Send Notification To' }} Key="title" onChange={() => {}} />
        <LoanPartiesEmailTo
          loanNumber={loanNumber}
          value={notifiers}
          data={[]}
          onChange={setNotifiers}
          loadParties
          modalId="addNoteNotification"
        />
        {error && <p className={`peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1`}>{error}</p>}
      </div>
    )
  }

  const placeholders = getPartiesMap({})

  return (
    <Modal
      title={item ? `Update Note - No.${index + 1}` : 'Create Email / Note'}
      titleOkay="Confirm"
      loading={loading}
      onClose={() => onClose()}
      onOk={onSubmit}
      childLevel={2}
      isOpen
    >
      <div className="text-gray-900">
        <div className="">
          <div className="my-4">
            <button
              type="button"
              className="border-b mb-2 w-full flex items-center gap-1 text-left"
              aria-expanded={placeholdersOpen}
              onClick={() => setPlaceholdersOpen((open) => !open)}
            >
              {placeholdersOpen ? (
                <ChevronDownIcon className="w-4 h-4 shrink-0" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 shrink-0" />
              )}
              <span>- Placeholders</span>
            </button>
            {placeholdersOpen && (
              <pre className="flex gap-1 flex-wrap">
                {Object.keys(placeholders)
                  .sort()
                  .map((key, index) => {
                    return (
                      <div
                        key={index}
                        className="mr-2 cursor-pointer hover:underline"
                        onClick={() => insertPlaceholder(key)}
                      >
                        {key},{' '}
                      </div>
                    )
                  })}
              </pre>
            )}
          </div>
          <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-2">
            {Object.keys(inputs).map((key, index) => {
              let input = inputs[key]
              if (key == 'notifiers') {
                if (item) return
                ;(input as CustomInput).render = renderNotifier
              }
              if (key === 'content') {
                const chooseTemplate = (
                  <span
                    className="text-shade-blue hover:underline cursor-pointer"
                    onClick={() => setShowTemplate(true)}
                  >
                    Choose Template
                  </span>
                )
                const aiBusy = aiLoading !== ''
                return (
                  <div className={`input md:col-span-${input.span || 1}`} key={index}>
                    {/* AI assist */}
                    <div className="mb-2 rounded-lg border border-violet-200 bg-violet-50/60 p-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[12px] font-semibold text-violet-700">✨ AI assist</span>
                        <input
                          type="text"
                          value={aiInstruction}
                          disabled={aiBusy}
                          placeholder={`Tell AI what to write… (e.g. "${
                            isEmail ? 'ask the borrower for updated insurance' : 'summarize the missed payment call'
                          }")`}
                          className="min-w-[180px] flex-1 rounded-md border border-violet-200 bg-white px-2.5 py-1 text-[13px] text-gray-800 placeholder:text-gray-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
                          onChange={(e) => setAiInstruction(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault()
                              onAiDraft()
                            }
                          }}
                        />
                        <button
                          type="button"
                          disabled={aiBusy || !aiInstruction.trim()}
                          onClick={onAiDraft}
                          className="rounded-md bg-violet-600 px-2.5 py-1 text-[12px] font-medium text-white transition hover:bg-violet-700 disabled:opacity-40"
                        >
                          {aiLoading === 'draft' ? 'Drafting…' : 'Draft'}
                        </button>
                        <button
                          type="button"
                          disabled={aiBusy || !stripHtml(input.value as string)}
                          onClick={onAiImprove}
                          className="rounded-md border border-violet-300 px-2.5 py-1 text-[12px] font-medium text-violet-700 transition hover:bg-violet-100 disabled:opacity-40"
                        >
                          {aiLoading === 'improve' ? 'Improving…' : 'Improve'}
                        </button>
                        <button
                          type="button"
                          disabled={aiBusy || !stripHtml(input.value as string)}
                          onClick={onAiSubject}
                          className="rounded-md border border-violet-300 px-2.5 py-1 text-[12px] font-medium text-violet-700 transition hover:bg-violet-100 disabled:opacity-40"
                        >
                          {aiLoading === 'subject' ? 'Working…' : 'Generate subject'}
                        </button>
                        {replyData && (
                          <button
                            type="button"
                            disabled={aiBusy}
                            onClick={onAiSummarize}
                            className="rounded-md border border-violet-300 px-2.5 py-1 text-[12px] font-medium text-violet-700 transition hover:bg-violet-100 disabled:opacity-40"
                          >
                            {aiLoading === 'summary' ? 'Summarizing…' : 'Summarize thread'}
                          </button>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-1.5">
                        <span className="text-[11px] text-gray-500">Tone:</span>
                        {(['Formal', 'Friendly', 'Firm'] as const).map((t) => (
                          <button
                            key={t}
                            type="button"
                            disabled={aiBusy}
                            onClick={() => setAiTone((prev) => (prev === t ? '' : t))}
                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition disabled:opacity-40 ${
                              aiTone === t
                                ? 'bg-violet-600 text-white'
                                : 'border border-violet-300 text-violet-700 hover:bg-violet-100'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                    <MentionRichText
                      value={(input.value as string) || ''}
                      onChange={(v) => onChange('content', v)}
                      users={mentionUsers}
                      groups={mentionGroups}
                      onAddMention={addMention}
                      onAddGroup={addGroup}
                      error={input.error}
                      title={input.title || 'Note'}
                      required={input.required}
                      additionalElements={chooseTemplate}
                      placeholders={Object.keys(placeholders).sort()}
                      onEditorReady={(ed) => (contentEditorRef.current = ed)}
                    />
                    <p className="text-[11px] text-gray-400 mt-1">
                      Tip: type @ to mention an internal user or a department — everyone tagged gets a task and email.
                    </p>
                  </div>
                )
              }
              return (
                <div className={`input md:col-span-${input.span || 1}`} key={index}>
                  <RenderInput input={input} Key={key} onChange={onChange} />
                </div>
              )
            })}
            {!item && <EmailLinksEditor value={links} onChange={setLinks} properties={{ showError }} span={2} />}
          </div>
        </div>
        {showTemplate && (
          <Modal
            title={templateType === 'note' ? 'Note Template' : 'Email Template'}
            titleOkay=""
            onClose={() => setShowTemplate(false)}
            isOpen
          >
            <div className="w-full max-w-[600px]">
              <div className="mb-3">
                <ButtonGroup
                  title={{ note: 'Note Templates', email: 'Email Templates' }}
                  value={templateType}
                  onChange={(v) => setTemplateType(v as 'note' | 'email')}
                />
              </div>
              {templateType === 'note' ? (
                <Notes isModal onRawClick={onChooseTemplate} notePageType={notePageType} />
              ) : (
                <EmailTemplates isModal onRawClick={onChooseTemplate} notePageType={notePageType} />
              )}
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  )
}
